import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

import { MentoringServices } from 'app/appServices/mentoring';
import { UtilityHelper } from 'app/helpers/utilityHelper';
import { ToastsManager } from 'app/helpers/toastsManager';
import { MentorListItem } from '../pages/gamification/models/mentor.model';
import { OnboardingProgressService } from '../pages/gamification/services/onboarding-progress.service';

export interface MentorMatchingState {
    selectedMentorId: string | null;
    pendingMentor: MentorListItem | null;
    matchedMentor: MentorListItem | null;
    isMatching: boolean;
    matchModalOpen: boolean;
    successModalOpen: boolean;
}

const initialState: MentorMatchingState = {
    selectedMentorId: null,
    pendingMentor: null,
    matchedMentor: null,
    isMatching: false,
    matchModalOpen: false,
    successModalOpen: false,
};

/**
 * Signal Store dedicated solely to the Mentee-Mentor Matching execution logic and matching state.
 */
export const MentorMatchingStore = signalStore(
    { providedIn: 'root' },
    withState<MentorMatchingState>(initialState),
    withComputed((store) => ({
        pendingMentorName: computed(() => store.pendingMentor()?.name ?? ''),
        matchedMentorName: computed(() => store.matchedMentor()?.name ?? ''),
    })),
    withMethods(
        (
            store,
            mentoringService = inject(MentoringServices),
            utility = inject(UtilityHelper),
            onboarding = inject(OnboardingProgressService),
            toaster = inject(ToastsManager),
        ) => ({
            selectMentorForMatch(mentor: MentorListItem): void {
                patchState(store, {
                    pendingMentor: mentor,
                    selectedMentorId: mentor.mentorId,
                    matchModalOpen: true,
                });
            },

            closeMatchModal(): void {
                patchState(store, {
                    matchModalOpen: false,
                    pendingMentor: null,
                });
            },

            closeSuccessModal(): void {
                patchState(store, {
                    successModalOpen: false,
                });
            },

            confirmMatch(toasterMessages: Record<string, string> = {}): void {
                const mentor = store.pendingMentor();
                if (!mentor || store.isMatching()) {
                    return;
                }

                patchState(store, {
                    isMatching: true,
                    selectedMentorId: mentor.mentorId,
                });

                const data = {
                    data: {
                        type: 'project_matches',
                        attributes: {
                            mentor_id: mentor.mentorId,
                            mentee_id: utility.getItem('projectUserId'),
                            project_id: utility.getItem('projectId'),
                        },
                    },
                };

                mentoringService.matchMentor(data).subscribe({
                    next: response => {
                        patchState(store, {
                            isMatching: false,
                            matchModalOpen: false,
                            pendingMentor: null,
                        });
                        if (response?.data) {
                            utility.setMentorChannelIdFromMatchApiResponse(response);
                            utility.setItem('isMenteeMatched', 'matched');
                            onboarding.markMentorMatched(mentor.name);
                            patchState(store, {
                                matchedMentor: mentor,
                                successModalOpen: true,
                            });
                        }
                    },
                    error: error => {
                        patchState(store, {
                            isMatching: false,
                            matchModalOpen: false,
                            pendingMentor: null,
                        });
                        const errorMessage =
                            error?.errors?.[0]?.title ||
                            `${toasterMessages['unableToMatchWith'] || 'Unable to match with'} ${mentor.name}`;
                        toaster.error(errorMessage);
                    },
                });
            },

            resetMatchState(): void {
                patchState(store, initialState);
            },
        }),
    ),
);
